import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { parseISO, differenceInCalendarDays } from 'date-fns';
import { formatAUDate } from '../../lib/dateUtils';

// Due-date buckets (columns). Order matters — left (most urgent) to right.
const COLUMNS = [
  { key: 'overdue',   title: 'Overdue',    accent: 'border-t-red-500',    head: 'text-red-700',    chip: 'bg-red-100 text-red-700' },
  { key: 'due_today', title: 'Due Today',  accent: 'border-t-orange-500', head: 'text-orange-700', chip: 'bg-orange-100 text-orange-700' },
  { key: 'this_week', title: 'This Week',  accent: 'border-t-amber-500',  head: 'text-amber-700',  chip: 'bg-amber-100 text-amber-700' },
  { key: 'this_month', title: 'This Month', accent: 'border-t-blue-500',  head: 'text-blue-700',   chip: 'bg-blue-100 text-blue-700' },
  { key: 'completed', title: 'Completed',  accent: 'border-t-green-500',  head: 'text-green-700',  chip: 'bg-green-100 text-green-700' },
];

const evDate = (d) => (typeof d.due_at === 'string' ? parseISO(d.due_at) : new Date(d.due_at));

// Decide which column an obligation belongs in, from its status + due date.
function bucketOf(d) {
  if (d.status === 'completed') return 'completed';
  if (!d.due_at) return 'this_month';
  const diff = differenceInCalendarDays(evDate(d), new Date());
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'due_today';
  if (diff <= 7) return 'this_week';
  return 'this_month'; // everything beyond a week (incl. further out) lives here
}

export default function ComplianceKanban({ deadlines = [], typeLabels = {} }) {
  const navigate = useNavigate();

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const d of deadlines) g[bucketOf(d)].push(d);
    // Sort each column by soonest due date first.
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (a.due_at && b.due_at ? evDate(a) - evDate(b) : 0));
    }
    return g;
  }, [deadlines]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {COLUMNS.map((col) => {
        const items = grouped[col.key];
        return (
          <div key={col.key} className={`bg-muted/50 rounded-xl border border-border border-t-4 ${col.accent} flex flex-col`} data-testid={`kanban-col-${col.key}`}>
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
              <span className={`font-semibold text-sm ${col.head}`}>{col.title}</span>
              <span className="text-xs bg-card border border-border rounded-full px-2 py-0.5 text-muted-foreground font-medium">{items.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground/70 text-center py-6">Nothing here</p>
              ) : (
                items.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => navigate('/compliance')}
                    className="w-full text-left bg-card rounded-lg border border-border p-3 hover:border-primary hover:shadow-sm transition-all"
                    data-testid={`kanban-card-${d.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-foreground text-sm">{typeLabels[d.type] || d.type}</span>
                      <Badge className={`${col.chip} text-[10px]`}>{col.title}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{d.client_name || '—'}</p>
                    {d.due_at && (
                      <p className="text-[11px] text-muted-foreground/70 mt-1">Due {formatAUDate(evDate(d))}</p>
                    )}
                    {d.workflow_status && (
                      <p className="text-[11px] text-muted-foreground mt-1 capitalize">{String(d.workflow_status).replace(/_/g, ' ')}</p>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
